import {
  useModalContext,
  WSButton,
  WSCheckboxToggle,
  WSIconList,
  WSModal,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayoutDestinationType,
  PayoutPreferences,
  VerificationStatus
} from "@wingspanhq/payments/dist/interfaces";
import React, { useState } from "react";
import { SetupFlowRouteComponentProps } from "../../../components/SetupFlow/SetupFlow";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import {
  QUERY_PAYMENTS_STATUS,
  QUERY_PAYOUT_SETTINGS
} from "../../../query/payments/keys";
import { WSQueries } from "../../../query/WSQuery";
import { paymentsService } from "../../../services/payments";
import { WS_LINKS } from "../../../types/wsLinks";
import { OnboardingOption } from "../../components/OnboardingOption/OnboardingOption";
import { PaymentsSetupLayout } from "../../components/PaymentsSetupLayout/PaymentsSetupLayout";
import { useVerificationUpgrade } from "../../components/VerificationUpgrade/VerificationUpgrade";

type DepositType = "instantDeposit" | "wallet" | "ach" | "giftCard";

export const SetupPayouts: React.FC<SetupFlowRouteComponentProps> = ({
  onBack,
  onNext
}) => {
  const userId = useUserId();
  const [depositType, setDepositType] = useState<DepositType | undefined>();
  const { openModal, closeModal } = useModalContext();
  const featureFlagsQuery = useFeatureFlags();
  const { requestVerification } = useVerificationUpgrade();
  const [agreement, setAgreement] = useState(false);

  const handleChange = (event: any) => {
    setDepositType(event.target.value);
  };

  const [submit, submitMeta] = useWSMutation(
    async () => {
      if (depositType === "wallet") {
        if (featureFlagsQuery.data?.verificationsByFeature) {
          await requestVerification({ banking: true });
        } else {
          await paymentsService.service.update(userId, {
            verifications: {
              taxDocumentation: VerificationStatus.Verified,
              banking: VerificationStatus.Verified,
              cardPayments: VerificationStatus.Verified
            }
          });
        }

        await paymentsService.payoutSettings.update(userId, {
          payoutPreferences: PayoutPreferences.Standard
        });
      } else if (depositType === "giftCard") {
        if (featureFlagsQuery.data?.verificationsByFeature) {
          await paymentsService.service.update(userId, {
            verifications: {
              taxDocumentation: VerificationStatus.Verified,
              banking: VerificationStatus.Verified,
              cardPayments: VerificationStatus.Verified
            }
          });
        }

        await paymentsService.payoutSettings.update(userId, {
          payoutPreferences: PayoutPreferences.Standard,
          standard: {
            destinationType: PayoutDestinationType.WeGift,
            destinationId: "WeGift"
          }
        });
      }

      return { depositType };
    },
    {
      dependencies: [QUERY_PAYMENTS_STATUS, QUERY_PAYOUT_SETTINGS],
      onSuccess: onNext
    }
  );

  return (
    <PaymentsSetupLayout
      onBack={onBack}
      title="Where should we deposit your income?"
    >
      <WSQueries queries={{ featureFlagsQuery }}>
        {({ featureFlagsQuery: { data: featureFlags } }) => (
          <>
            <OnboardingOption
              mb="S"
              name="depositType"
              value="instantDeposit"
              onChange={handleChange}
              title={
                <>
                  Instant to Debit Card <WSText inline>(1% fee)</WSText>
                </>
              }
            >
              <WSText>
                Fastest. Receive your payments immediately when sent.
              </WSText>
            </OnboardingOption>

            <OnboardingOption
              mb="S"
              name="depositType"
              value="wallet"
              onChange={handleChange}
              title={
                <>
                  Wingspan Wallet <WSText inline>(free)</WSText>
                </>
              }
            >
              <WSText>
                Faster. Free business bank & debit card with 1% cashback.{" "}
                <WSButton.Link
                  inline
                  onClick={() => {
                    openModal("WS_WALLET_INFO");
                  }}
                >
                  Learn more
                </WSButton.Link>
              </WSText>
            </OnboardingOption>

            <OnboardingOption
              mb="M"
              name="depositType"
              value="ach"
              onChange={handleChange}
              title={
                <>
                  Bank Account <WSText inline>(free)</WSText>
                </>
              }
            >
              <WSText>Standard. Connect your existing bank account.</WSText>
            </OnboardingOption>

            {featureFlags.giftCards && (
              <OnboardingOption
                mb="M"
                name="depositType"
                value="giftCard"
                onChange={handleChange}
                title={
                  <>
                    Gift card <WSText inline>(free)</WSText>
                  </>
                }
              >
                <WSText>Standard. 30+ available brands.</WSText>
              </OnboardingOption>
            )}

            <WSCheckboxToggle
              mt="XL"
              mb="M"
              name="agreement"
              label={
                <>
                  I agree to the{" "}
                  <a
                    href={WS_LINKS.depositAccountTermsConditions}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Deposit Account Terms and Conditions
                  </a>
                  ,{" "}
                  <a
                    href={WS_LINKS.electronicDisclosure}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Electronic Disclosure and Consent
                  </a>
                  , and{" "}
                  <a href={WS_LINKS.TCCITerms} target="_blank" rel="noreferrer">
                    TCCI Terms of Use
                  </a>
                  .
                </>
              }
              value={agreement}
              onChange={value => {
                setAgreement(value);
              }}
            />

            <WSButton
              mb="M"
              name="continue"
              onClick={submit}
              loading={submitMeta.isLoading}
              disabled={!depositType || !agreement}
              fullWidth
            >
              Continue
            </WSButton>

            <WSText.Heading5 color="grey700" mb="S">
              We work with Stripe to power credit payments. By continuing, you
              agree to{" "}
              <a
                href={WS_LINKS.stripeConnectedTerms}
                target="_blank"
                rel="noreferrer"
              >
                Stripe Connected Account Agreement
              </a>
              .
            </WSText.Heading5>

            <WSText.Heading5 color="grey700">
              Wingspan is a financial services technology company and is not a
              bank. Banking services provided by Blue Ridge Bank; Member FDIC.
            </WSText.Heading5>

            <WSModal name="WS_WALLET_INFO" size="S">
              <WSText.Heading3 mb="S">Wingspan Wallet</WSText.Heading3>
              <WSText mb="S">
                Wingspan Wallet is an optional & free bank account and debit
                card for your business. You can choose how much of your income
                you want to set aside in your Wingspan Wallet, and how much you
                would like deposited to your bank account.
              </WSText>

              <WSIconList
                iconProps={{ name: "checkLine" }}
                items={[
                  "Receive your payments 1-2 day sooner",
                  "Automatic write-offs of business expenses, fully integrated with Wingspan Bookkeeping",
                  "No account, transaction, minimum balance or hidden fees",
                  "Unlimited ACH and wire transfers for free",
                  "Fee-free cash deposits and withdrawals at 40,000 ATMs nationwide."
                ]}
              />

              <WSButton.Link
                mt="M"
                onClick={() => {
                  closeModal("WS_WALLET_INFO");
                }}
                icon="arrowLeft"
              >
                Get back
              </WSButton.Link>
            </WSModal>
          </>
        )}
      </WSQueries>
    </PaymentsSetupLayout>
  );
};
